import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

export const FILE_TYPES = ["Documents", "Images", "Code", "Spreadsheets"] as const;
export const MACHINES = ["i9", "MacBook", "iDrive", "Cloud"] as const;
export const DATE_RANGES = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "All", days: 0 },
] as const;

export interface FilterState {
  types: string[];
  machines: string[];
  dateDays: number;
}

interface FiltersProps {
  state: FilterState;
  onChange: (s: FilterState) => void;
}

function Chip({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "px-3 py-1.5 rounded-full text-xs font-medium border transition-all " +
        (active
          ? "bg-accent-blue/15 border-accent-blue/40 text-accent-blue"
          : "bg-white/[0.02] border-white/[0.06] text-slate-400 hover:bg-white/[0.05] hover:border-white/[0.12] hover:text-slate-200")
      }
    >
      {children}
    </button>
  );
}

export default function Filters({ state, onChange }: FiltersProps) {
  const [open, setOpen] = useState(false);

  const toggle = (list: string[], v: string) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const activeCount =
    state.types.length + state.machines.length + (state.dateDays ? 1 : 0);

  return (
    <div className="glass ring-glass overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-slate-300 hover:bg-white/[0.02]"
      >
        <span className="flex items-center gap-2">
          <SlidersHorizontal size={14} strokeWidth={2} className="text-slate-500" />
          <span className="font-medium">Filters</span>
          {activeCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-accent-blue/15 text-accent-blue text-[10px] font-mono">
              {activeCount}
            </span>
          )}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-2 space-y-4">
              <div>
                <div className="eyebrow mb-2">File type</div>
                <div className="flex flex-wrap gap-2">
                  <Chip
                    active={state.types.length === 0}
                    onClick={() => onChange({ ...state, types: [] })}
                  >
                    All
                  </Chip>
                  {FILE_TYPES.map((t) => (
                    <Chip
                      key={t}
                      active={state.types.includes(t)}
                      onClick={() => onChange({ ...state, types: toggle(state.types, t) })}
                    >
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">Machine</div>
                <div className="flex flex-wrap gap-2">
                  <Chip
                    active={state.machines.length === 0}
                    onClick={() => onChange({ ...state, machines: [] })}
                  >
                    All
                  </Chip>
                  {MACHINES.map((m) => (
                    <Chip
                      key={m}
                      active={state.machines.includes(m)}
                      onClick={() => onChange({ ...state, machines: toggle(state.machines, m) })}
                    >
                      {m}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <div className="eyebrow mb-2">Modified</div>
                <div className="flex flex-wrap gap-2">
                  {DATE_RANGES.map((r) => (
                    <Chip
                      key={r.label}
                      active={state.dateDays === r.days}
                      onClick={() => onChange({ ...state, dateDays: r.days })}
                    >
                      {r.days ? `Last ${r.label}` : "All time"}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
